import { useEffect, useRef, useState } from 'react';
import type { CallInfo } from './call.js';
import { useT } from './i18n.js';
import {
  AcceptCallIcon,
  BackIcon,
  CameraIcon,
  Avatar,
  FullscreenIcon,
  HangUpIcon,
  MicIcon,
  SwitchCameraIcon,
} from './ui.js';

/**
 * Anrufoberfläche.
 *
 * Liegt als Overlay über dem Chat, damit der Gesprächszustand immer sichtbar
 * bleibt — ein laufender Anruf, den man beim Weiterklicken aus den Augen
 * verliert, ist bei einem Mikrofon-Zugriff die falsche Wahl.
 */
export function CallOverlay({
  info,
  localStream,
  remoteStream,
  onAccept,
  onHangUp,
  onReject,
  onToggleMute,
  onSwitchCamera,
  onToggleCamera,
  onMinimise,
}: {
  info: CallInfo;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  onAccept: () => void;
  onHangUp: () => void;
  onReject: () => void;
  onToggleMute: () => boolean;
  onSwitchCamera: () => void;
  onToggleCamera: () => boolean;
  onMinimise: (value: boolean) => void;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const localVideo = useRef<HTMLVideoElement>(null);
  const remoteVideo = useRef<HTMLVideoElement>(null);
  const remoteAudio = useRef<HTMLAudioElement>(null);
  const t = useT();
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // Der Vollbildmodus lässt sich auch mit Escape oder der Zurück-Geste
  // verlassen, ohne dass unser Knopf davon erfährt. Ohne diesen Abgleich
  // zeigte das Symbol danach den falschen Zustand.
  //
  // Wichtiger noch: Browser pausieren Video-Elemente beim Wechsel in und aus
  // dem Vollbild. `autoplay` greift nur beim ersten Anhängen des Streams,
  // nicht nach einer Pause — das Bild bliebe stehen. Und weil im Videoanruf
  // der TON der Gegenseite aus demselben Element kommt (das <audio>-Element
  // gibt es nur im Sprachanruf), wäre danach auch nichts mehr zu hören.
  useEffect(() => {
    const sync = () => {
      setFullscreen(document.fullscreenElement !== null);
      resume();
    };
    document.addEventListener('fullscreenchange', sync);
    // iOS meldet den Wechsel am Video-Element statt am Dokument.
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  /**
   * Wiedergabe fortsetzen. Ein Anruf kennt keine gewollte Pause — steht ein
   * Element still, ist das immer ein Nebeneffekt von Vollbild, Tabwechsel
   * oder Energiesparen, nie eine Absicht der Nutzerin.
   */
  function resume() {
    for (const el of [remoteVideo.current, localVideo.current, remoteAudio.current]) {
      if (el?.paused) void el.play().catch(() => {});
    }
  }

  async function toggleFullscreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => {});
      return;
    }

    // Vollbild auf die ganze Anrufbox, nicht nur auf das Video: Sonst lägen
    // Auflegen und Kamerawechsel außerhalb des sichtbaren Bereichs und man
    // käme nur noch über Escape heraus.
    const box = stage.current;
    if (box?.requestFullscreen) {
      await box.requestFullscreen().catch(() => {});
      return;
    }

    // iPhone: Safari kennt requestFullscreen nur auf dem Video-Element selbst,
    // nicht auf beliebigen Elementen. Dann geht nur das Bild der Gegenseite
    // ins Vollbild, ohne unsere Bedienleiste.
    const video = remoteVideo.current as (HTMLVideoElement & {
      webkitEnterFullscreen?: () => void;
    }) | null;
    video?.webkitEnterFullscreen?.();
  }

  // info.video und remoteStream gehören in die Abhängigkeiten: Das
  // Video-Element entsteht erst, wenn beides passt. Ohne sie liefe dieser
  // Effekt nur einmal — zu einem Zeitpunkt, an dem localVideo.current noch
  // null ist — und das eigene Bild bliebe dauerhaft schwarz.
  useEffect(() => {
    if (localVideo.current) localVideo.current.srcObject = localStream;
  }, [localStream, info.video, remoteStream]);

  useEffect(() => {
    // Video-Element bei Videoanrufen, Audio-Element sonst. Ohne das zweite
    // bliebe ein reiner Sprachanruf stumm.
    if (remoteVideo.current) remoteVideo.current.srcObject = remoteStream;
    if (remoteAudio.current) remoteAudio.current.srcObject = remoteStream;
  }, [remoteStream, info.video]);

  if (info.state === 'idle') return null;

  /*
   * Eingeklappt: nur eine schmale Leiste, der Anruf laeuft weiter.
   *
   * Wie man es vom Telefon kennt — waehrend eines Gespraechs etwas nachlesen
   * zu koennen, ohne aufzulegen. Die Leiste fuehrt mit einem Tippen zurueck;
   * Auflegen bleibt ein eigener Knopf, denn die beiden zu vermischen kostet
   * irgendwann ein Gespraech.
   */
  if (info.minimised) {
    return (
      <>
        {!info.video && <audio ref={remoteAudio} autoPlay onPause={resume} />}
        <button type="button" className="call-bar" onClick={() => onMinimise(false)}>
          <span className="call-bar-dot" aria-hidden="true" />
          <span className="call-bar-text">
            {t('ongoingCall')} · {info.peerName}
          </span>
          <span className="call-bar-action">{t('returnToCall')}</span>
        </button>
      </>
    );
  }

  const label =
    info.state === 'calling'
      ? t('calling')
      : info.state === 'ringing'
        ? t('incomingCall')
        : info.state === 'connecting'
          ? t('connecting')
          : info.state === 'connected'
            ? t('connected')
            : t('callEnded');

  return (
    <div className="call" ref={stage}>
      {/*
        Vollflächig statt als Karte — wie man es von Telefon-Apps kennt: Das
        Bild der Gegenstelle füllt den Schirm, das eigene sitzt klein in der
        Ecke, Name oben und Bedienleiste unten schweben darüber.

        Der Grund ist nicht Gewohnheit: Ein Gesichtsausdruck in einem 420px
        breiten Kästchen ist schwerer zu lesen als über die volle Fläche, und
        ein Anruf ist der einzige Moment, in dem nichts anderes wichtig ist.
      */}
      <div className="call-stage">
        {info.video && remoteStream ? (
          <video
            ref={remoteVideo}
            autoPlay
            playsInline
            onPause={resume}
            className="call-remote"
          />
        ) : (
          <div className="call-avatar">
            <Avatar name={info.peerName || '?'} />
          </div>
        )}

        {/* Das eigene Bild erscheint, sobald die Kamera läuft — also schon
            während "Ruft an…". Es an remoteStream zu koppeln hieße: Man sieht
            sich erst, wenn der andere abnimmt, und weiß bis dahin nicht, ob
            die Kamera überhaupt etwas aufnimmt. */}
        {info.video && localStream && !info.cameraOff && (
          <video
            ref={localVideo}
            autoPlay
            playsInline
            muted
            onPause={resume}
            className="call-local"
          />
        )}
      </div>

      {/* Ton der Gegenseite — im Videofall hängt derselbe Stream schon am
          Video-Element, deshalb nur im Sprachanruf. */}
      {!info.video && <audio ref={remoteAudio} autoPlay onPause={resume} />}

      <div className="call-top">
        {/* Nur im laufenden Gespraech: Beim Klingeln waere ein Zurueck
            zweideutig — annehmen oder ablehnen ist dort die Frage. */}
        {(info.state === 'connected' || info.state === 'connecting') && (
          <button
            type="button"
            className="call-back"
            onClick={() => onMinimise(true)}
            title={t('minimise')}
            aria-label={t('minimise')}
          >
            <BackIcon />
          </button>
        )}
        <h2 className="call-name">{info.peerName}</h2>
        <p className="call-state">{label}</p>
        {info.error && <div className="alert alert-error call-error">{info.error}</div>}
      </div>

      <div className="call-bottom">
        {/* Der Hinweis steht nur, solange noch niemand spricht. Während des
            Gesprächs nimmt er Platz weg und sagt nichts Neues. */}
        {info.state !== 'connected' && <p className="call-note">{t('callNote')}</p>}

        <div className="call-actions">
          {info.state === 'ringing' ? (
            <>
              <button
                className="call-btn call-reject"
                onClick={onReject}
                title={t('reject')}
                aria-label={t('reject')}
              >
                <HangUpIcon />
              </button>
              <button
                className="call-btn call-accept"
                onClick={onAccept}
                title={t('accept')}
                aria-label={t('accept')}
              >
                <AcceptCallIcon />
              </button>
            </>
          ) : info.state === 'ended' ? null : (
            <>
              <button
                className={muted ? 'call-btn call-muted' : 'call-btn call-secondary'}
                onClick={() => setMuted(onToggleMute())}
                title={muted ? t('muteOn') : t('muteOff')}
                aria-label={muted ? t('muteOn') : t('muteOff')}
              >
                <MicIcon off={muted} />
              </button>

              {/* Kamerawechsel und Vollbild nur im Videoanruf — im Sprachanruf
                  gibt es nichts zu drehen und nichts zu vergrößern. */}
              {info.video && (
                <>
                  <button
                    className={
                      info.cameraOff ? 'call-btn call-muted' : 'call-btn call-secondary'
                    }
                    onClick={onToggleCamera}
                    title={info.cameraOff ? t('cameraOn') : t('cameraOff')}
                    aria-label={info.cameraOff ? t('cameraOn') : t('cameraOff')}
                  >
                    <CameraIcon off={info.cameraOff} />
                  </button>
                  <button
                    className="call-btn call-secondary"
                    onClick={onSwitchCamera}
                    title={t('switchCamera')}
                    aria-label={t('switchCamera')}
                  >
                    <SwitchCameraIcon />
                  </button>
                  <button
                    className="call-btn call-secondary"
                    onClick={() => void toggleFullscreen()}
                    title={fullscreen ? t('exitFullscreen') : t('fullscreen')}
                    aria-label={fullscreen ? t('exitFullscreen') : t('fullscreen')}
                  >
                    <FullscreenIcon exit={fullscreen} />
                  </button>
                </>
              )}

              <button
                className="call-btn call-reject"
                onClick={onHangUp}
                title={t('hangUp')}
                aria-label={t('hangUp')}
              >
                <HangUpIcon />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
